import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faCheck, faTimes, faClose, faSearch, faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { useGlobalData } from '../../contexts/GlobalDataContext';

type SimpleDropdownCategory = 'contentUnit' | 'unit' | 'baseUnit' | 'baseComparisonUnitCode' | 'unitDescriptor' | 'country' | 'storageZone' | 'packagingUnit' | 'fangzone' | 'fangmethode' | 'line' | 'markantUnitDescriptor' | 'dangerousGoods';

interface SimpleDropdownPopupProps {
  category: SimpleDropdownCategory;
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  allowAdminMode?: boolean;
  className?: string;
}

const SimpleDropdownPopup: React.FC<SimpleDropdownPopupProps> = ({
  category,
  value,
  onChange,
  placeholder = 'Auswählen...',
  label,
  required = false,
  disabled = false,
  allowAdminMode = true,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [newOptionLabel, setNewOptionLabel] = useState('');
  const [newOptionValue, setNewOptionValue] = useState('');
  const [editingOption, setEditingOption] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editValue, setEditValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Use GlobalDataContext for all data
  const globalData = useGlobalData();
  
  // Get data from GlobalDataContext
  const loading = globalData.isLoading;
  const dropdownOptions = globalData.dropdownData[category as keyof typeof globalData.dropdownData];
  const options = Array.isArray(dropdownOptions) ? dropdownOptions : [];

  const selectedOption = options.find(opt => opt.value === value);

  const getDisplayText = () => {
    if (loading) return 'Wird geladen...';
    return selectedOption ? selectedOption.label_DE : (value || placeholder);
  };

  const getCategoryDisplayName = () => {
    switch (category) {
      case 'unitDescriptor': return 'Einheit auswählen';
      case 'packagingUnit': return 'Verpackung auswählen';
      case 'unit': return 'Einheit auswählen';
      case 'contentUnit': return 'Inhaltseinheit auswählen';
      case 'baseUnit': return 'Basiseinheit auswählen';
      case 'baseComparisonUnitCode': return 'Vergleichseinheit auswählen';
      case 'country': return 'Land auswählen';
      case 'storageZone': return 'Lagerzone auswählen';
      case 'fangzone': return 'Fangzone auswählen';
      case 'fangmethode': return 'Fangmethode auswählen';
      case 'line': return 'Linie auswählen';
      case 'markantUnitDescriptor': return 'Markant Einheit auswählen';
      case 'dangerousGoods': return 'Gefahrgut auswählen';
      default: return 'Option auswählen';
    }
  };

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleCreateOption = async () => {
    if (!newOptionLabel.trim() || !newOptionValue.trim()) return;

    try {
      // TODO: Implement create option functionality via GlobalDataContext
      console.log('Create option not implemented yet:', {
        category,
        value: newOptionValue.trim(),
        label: newOptionLabel.trim()
      });
      
      setNewOptionLabel('');
      setNewOptionValue('');
    } catch (error) {
      console.error('Fehler beim Erstellen der Option:', error);
    }
  };

  const handleUpdate = async (optionId: string) => {
    if (!editLabel.trim() || !editValue.trim()) return;

    try {
      // TODO: Implement update option functionality via GlobalDataContext
      console.log('Update option not implemented yet:', {
        optionId,
        label: editLabel.trim(),
        value: editValue.trim()
      });
      setEditingOption(null);
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Option:', error);
    }
  };

  const handleDelete = async (optionId: string) => {
    if (window.confirm('Diese Option wirklich löschen?')) {
      try {
        // TODO: Implement delete option functionality via GlobalDataContext
        console.log('Delete option not implemented yet:', optionId);
      } catch (error) {
        console.error('Fehler beim Löschen der Option:', error);
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const filteredOptions = searchTerm
    ? options.filter(opt =>
        opt.label_DE?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        opt.value?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  return (
    <>
      <div className={`relative ${className}`}>
        {label && (
          <label className="block text-xs font-medium text-gray-700 mb-1">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(true)}
          disabled={disabled}
          className={`
            relative w-full h-[36px] bg-white border border-gray-300 rounded-xl pl-4 pr-12 py-1.5 text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all duration-200
            ${disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : 'hover:border-gray-400 hover:shadow-sm'}
          `}
        >
          <span className="block truncate text-sm">{getDisplayText()}</span>
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center">
              <FontAwesomeIcon 
                icon={faChevronDown} 
                className="w-2.5 h-2.5 text-gray-500"
              />
            </div>
          </div>
        </button>
      </div>

      {/* Modern Popup Modal */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsOpen(false);
              setIsAdminMode(false);
              setSearchTerm('');
            }
          }}
        >
          <div 
            className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[70vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50">
              <h3 className="text-lg font-semibold text-gray-900">
                {getCategoryDisplayName()}
              </h3>
              <div className="flex items-center gap-2">
                {allowAdminMode && (
                  <button
                    onClick={() => setIsAdminMode(!isAdminMode)}
                    className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 ${
                      isAdminMode 
                        ? 'bg-blue-500 text-white shadow-md' 
                        : 'bg-white text-gray-500 hover:bg-gray-50 shadow-sm border border-gray-200'
                    }`}
                  >
                    <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => {
                    setIsOpen(false);
                    setIsAdminMode(false);
                    setSearchTerm('');
                  }}
                  className="w-9 h-9 bg-white text-gray-500 rounded-full flex items-center justify-center hover:bg-gray-50 transition-all duration-200 shadow-sm border border-gray-200"
                >
                  <FontAwesomeIcon icon={faClose} className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Search */}
            <div className="px-6 py-4">
              <div className="relative">
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                  <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
                    <FontAwesomeIcon icon={faSearch} className="w-3 h-3 text-gray-500" />
                  </div>
                </div>
                <input
                  type="text"
                  placeholder="Suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-14 pr-4 py-3 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 transition-all duration-200 text-sm"
                />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : filteredOptions.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FontAwesomeIcon icon={faSearch} className="w-6 h-6 text-gray-400" />
                  </div>
                  <p className="text-gray-500 text-sm">
                    {searchTerm ? 'Keine Ergebnisse gefunden' : 'Keine Optionen verfügbar'}
                  </p>
                </div>
              ) : (
                <div className="space-y-1 pb-4">
                  {filteredOptions.map((option) => (
                    <div key={option._id || option.value}>
                      {editingOption !== option._id ? (
                        <div className="flex items-center group rounded-xl hover:bg-gray-50 transition-all duration-200">
                          <button
                            onClick={() => handleSelect(option.value)}
                            className={`
                              flex-1 text-left px-4 py-3 rounded-xl transition-all duration-200
                              ${value === option.value ? 'bg-blue-50 text-blue-900 font-medium' : 'text-gray-900'}
                            `}
                          >
                            {option.label_DE || option.value}
                          </button>
                          
                          {isAdminMode && (
                            <div className="flex items-center gap-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                              <button
                                onClick={() => {
                                  setEditingOption(option._id);
                                  setEditLabel(option.label_DE);
                                  setEditValue(option.value);
                                }}
                                className="w-7 h-7 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center hover:bg-blue-200 transition-colors duration-200"
                              >
                                <FontAwesomeIcon icon={faEdit} className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => handleDelete(option._id)}
                                className="w-7 h-7 bg-red-100 text-red-600 rounded-full flex items-center justify-center hover:bg-red-200 transition-colors duration-200"
                              >
                                <FontAwesomeIcon icon={faTrash} className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                          <div className="flex gap-3">
                            <input
                              type="text"
                              value={editLabel}
                              onChange={(e) => setEditLabel(e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Anzeigename"
                            />
                            <input
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Wert"
                            />
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleUpdate(option._id)}
                              className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors duration-200 text-sm font-medium"
                            >
                              <FontAwesomeIcon icon={faCheck} className="w-3 h-3 mr-2" />
                              Speichern
                            </button>
                            <button
                              onClick={() => setEditingOption(null)}
                              className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-xl hover:bg-gray-400 transition-colors duration-200 text-sm font-medium"
                            >
                              <FontAwesomeIcon icon={faTimes} className="w-3 h-3 mr-2" />
                              Abbrechen
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Admin Footer */}
            {isAdminMode && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <input
                      type="text"
                      placeholder="Anzeigename"
                      value={newOptionLabel}
                      onChange={(e) => setNewOptionLabel(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Wert"
                      value={newOptionValue}
                      onChange={(e) => setNewOptionValue(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                  <button
                    onClick={handleCreateOption}
                    disabled={!newOptionLabel.trim() || !newOptionValue.trim()}
                    className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:from-blue-600 hover:to-indigo-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 font-medium text-sm"
                  >
                    <FontAwesomeIcon icon={faPlus} className="w-4 h-4 mr-2" />
                    Neue Option hinzufügen
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export { SimpleDropdownPopup };
export default SimpleDropdownPopup;